import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ComparableUnit, ReferenceUnit } from '../super-picker/services/api';
import HuttonsWhiteLogo from '../img/Huttons_white.png';
import { isUnitPricingHidden } from './formatting';
import { ensurePdfUnicodeFont, PDF_UNICODE_FONT } from './pdfUnicodeFont';
import { savePdfDocument } from '../super-calculator/utils/savePdfDocument';

const CORPORATE_COLOR_RGB: [number, number, number] = [26, 35, 126];
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];

interface UserInfo {
  Name?: string;
  Mobile?: string;
  Email?: string;
  CEANumber?: string;
  Photo?: string;
}

interface FloorPlanCompareUnit {
  unit: ComparableUnit | ReferenceUnit;
  imageUrl: string;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || isNaN(value) || value === 0) return '-';
  return `$${value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (value: number | null | undefined, decimals: number = 0): string => {
  if (value == null || isNaN(value)) return '-';
  return value.toLocaleString('en-SG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const parseUnitNumber = (unitNumber: string): { floor: number | null; stack: number | null } => {
  const match = unitNumber?.match(/#?(\d+)-(\d+)/);
  if (!match) return { floor: null, stack: null };
  return { floor: parseInt(match[1], 10), stack: parseInt(match[2], 10) };
};

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('Image load timeout')), 10000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error('Image load failed')); };
    img.src = url;
  });
};

const loadUserPhoto = async (
  doc: jsPDF, userInfo: UserInfo,
  photoX: number, photoY: number, photoRadius: number,
): Promise<boolean> => {
  if (!userInfo.Photo) return false;
  try {
    const photoPath = userInfo.Photo.startsWith('/') ? userInfo.Photo.substring(1) : userInfo.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = await loadImage(photoUrl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const size = photoRadius * 2;
    const px = Math.max(256, Math.round(size * 12));
    canvas.width = px;
    canvas.height = px;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const r = px / 2;
    const maxInCircle = r * Math.SQRT2;
    const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
    const sw = Math.round(img.width * scale);
    const sh = Math.round(img.height * scale);
    const sx = Math.round((px - sw) / 2);
    const sy = Math.round((px - sh) / 2);
    ctx.save();
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, sx, sy, sw, sh);
    ctx.restore();
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', photoX - photoRadius, photoY - photoRadius, size, size);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(1);
    doc.circle(photoX, photoY, photoRadius, 'D');
    return true;
  } catch {
    return false;
  }
};

/** Renders a consistent header with user photo + info on the right */
const renderHeaderWithUser = async (
  doc: jsPDF, title: string, userInfo: UserInfo | null,
): Promise<number> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = userInfo ? 20 : 12;

  // Orange bar
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 6, 'F');
  // Blue header
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.rect(0, 6, pageWidth, headerHeight, 'F');
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text(title, 10, 6 + headerHeight / 2 + 2);

  if (userInfo) {
    const photoRadius = 7;
    const photoX = pageWidth - 18;
    const photoY = 6 + headerHeight / 2;

    // Text to the left of photo
    const textRightEdge = photoX - photoRadius - 4;
    doc.setFontSize(9);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(...ORANGE_RGB);
    if (userInfo.Name) doc.text(userInfo.Name, textRightEdge, photoY - 3, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    doc.setFontSize(7);
    const details: string[] = [];
    if (userInfo.CEANumber) details.push(`CEA: ${userInfo.CEANumber}`);
    if (userInfo.Mobile) details.push(userInfo.Mobile);
    if (details.length > 0) doc.text(details.join('  |  '), textRightEdge, photoY + 3, { align: 'right' });

    // Photo circle
    const photoLoaded = await loadUserPhoto(doc, userInfo, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      const initials = userInfo.Name ? userInfo.Name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
      doc.text(initials, photoX, photoY + 1.5, { align: 'center' });
    }
  }

  return 6 + headerHeight + 4;
};

export interface FloorPlanComparisonPdfSection {
  title: string;
  /** Pre-captured PNG data URLs, indexed like `floorPlans`; a gap falls back to `fallbackImageUrls`. */
  screenshots?: Array<string | null | undefined>;
  /** Plain images to embed when there is no screenshot. Defaults to each unit's floor plan. */
  fallbackImageUrls?: Array<string | null | undefined>;
}

export const generateFloorPlanComparisonPdf = async (
  floorPlans: FloorPlanCompareUnit[],
  userInfo: UserInfo | null,
  screenshots?: Array<string | null | undefined>,
  options?: { title?: string; sections?: FloorPlanComparisonPdfSection[]; fileNamePrefix?: string },
): Promise<void> => {
  if (floorPlans.length === 0) return;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await ensurePdfUnicodeFont(doc);

  const sections = options?.sections?.length
    ? options.sections
    : [{ title: options?.title ?? 'FLOOR PLAN COMPARISON', screenshots }];

  for (let s = 0; s < sections.length; s++) {
    if (s > 0) doc.addPage();
    await renderComparisonPage(doc, floorPlans, userInfo, sections[s]);
  }

  // ── Save ──
  const unitNames = floorPlans.map(fp => fp.unit.unitNumber.replace(/[^a-zA-Z0-9-]/g, '')).join('_vs_');
  const prefix = options?.fileNamePrefix ?? 'Floor_Plan_Comparison';
  const fileName = `${prefix}_${unitNames}_${new Date().toISOString().split('T')[0]}.pdf`;
  await savePdfDocument(doc, fileName);
};

const renderComparisonPage = async (
  doc: jsPDF,
  floorPlans: FloorPlanCompareUnit[],
  userInfo: UserInfo | null,
  section: FloorPlanComparisonPdfSection,
): Promise<void> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const { screenshots } = section;

  let yPos = await renderHeaderWithUser(doc, section.title, userInfo);

  // ── Plan images side by side (aligned with OVERVIEW table below) ──
  const count = floorPlans.length;
  const margin = 10;
  const totalWidth = pageWidth - margin * 2;
  const labelColWidth = 30;
  const dataColWidth = (totalWidth - labelColWidth) / count;
  const imgMaxHeight = screenshots?.some(Boolean) ? 95 : 75;

  for (let i = 0; i < count; i++) {
    const fp = floorPlans[i];
    const fallbackImageUrl = section.fallbackImageUrls ? section.fallbackImageUrls[i] : fp.imageUrl;
    const x = margin + labelColWidth + i * dataColWidth;

    // Label above image
    doc.setFontSize(9);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(fp.unit.projectName || '-', x + dataColWidth / 2, yPos + 3, { align: 'center' });
    doc.setFontSize(11);
    doc.text(fp.unit.unitNumber || '-', x + dataColWidth / 2, yPos + 8, { align: 'center' });

    // Image box
    const imgY = yPos + 11;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(x, imgY, dataColWidth, imgMaxHeight, 'S');

    const screenshotDataUrl = screenshots?.[i];
    if (screenshotDataUrl) {
      // Use pre-captured screenshot (includes drawings/annotations)
      try {
        const img = await loadImage(screenshotDataUrl);
        const aspectRatio = img.width / img.height;
        let drawWidth = dataColWidth - 4;
        let drawHeight = drawWidth / aspectRatio;
        if (drawHeight > imgMaxHeight - 4) {
          drawHeight = imgMaxHeight - 4;
          drawWidth = drawHeight * aspectRatio;
        }
        const imgX = x + (dataColWidth - drawWidth) / 2;
        const imgDrawY = imgY + (imgMaxHeight - drawHeight) / 2;
        doc.addImage(screenshotDataUrl, 'PNG', imgX, imgDrawY, drawWidth, drawHeight);
      } catch {
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(8);
        doc.text('Image unavailable', x + dataColWidth / 2, imgY + imgMaxHeight / 2, { align: 'center' });
      }
    } else if (fallbackImageUrl) {
      try {
        const img = await loadImage(fallbackImageUrl);
        const aspectRatio = img.width / img.height;
        let drawWidth = dataColWidth - 4;
        let drawHeight = drawWidth / aspectRatio;
        if (drawHeight > imgMaxHeight - 4) {
          drawHeight = imgMaxHeight - 4;
          drawWidth = drawHeight * aspectRatio;
        }
        const imgX = x + (dataColWidth - drawWidth) / 2;
        const imgDrawY = imgY + (imgMaxHeight - drawHeight) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          doc.addImage(dataUrl, 'JPEG', imgX, imgDrawY, drawWidth, drawHeight);
        }
      } catch {
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(8);
        doc.text('Image unavailable', x + dataColWidth / 2, imgY + imgMaxHeight / 2, { align: 'center' });
      }
    } else {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.text('Image unavailable', x + dataColWidth / 2, imgY + imgMaxHeight / 2, { align: 'center' });
    }
  }

  yPos += 11 + imgMaxHeight + 6;

  // ── OVERVIEW table (column positions match floor plan section above) ──
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.roundedRect(margin, yPos, totalWidth, 8, 1, 1, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text('OVERVIEW', pageWidth / 2, yPos + 5.5, { align: 'center' });
  yPos += 10;

  const overviewRows: Array<{ label: string; key: string }> = [
    { label: 'Price', key: 'price' },
    { label: 'PSF', key: 'psf' },
    { label: 'Outlay', key: 'outlay' },
    { label: 'Installment', key: 'installment' },
    { label: 'Area', key: 'areaSqft' },
    { label: 'Bedroom', key: 'bedroom' },
    { label: 'Bathroom', key: 'bathroom' },
    { label: 'Block', key: 'block' },
    { label: 'Floor', key: 'floor' },
    { label: 'Stack', key: 'stack' },
  ];

  const isUnitPriceHidden = (unit: ComparableUnit | ReferenceUnit): boolean =>
    isUnitPricingHidden(unit.unitStatus);

  const getUnitValue = (unit: ComparableUnit | ReferenceUnit, key: string): string | number => {
    const parsed = parseUnitNumber(unit.unitNumber || '');
    switch (key) {
      case 'price': return isUnitPriceHidden(unit) ? 0 : (unit.price ?? 0);
      case 'psf': return isUnitPriceHidden(unit) ? 0 : (unit.psf ?? (unit.price && unit.areaSqft ? unit.price / unit.areaSqft : 0));
      case 'outlay': return isUnitPriceHidden(unit) ? 0 : (unit.outlay ?? 0);
      case 'installment': return isUnitPriceHidden(unit) ? 0 : (unit.installment ?? 0);
      case 'areaSqft': return unit.areaSqft ?? 0;
      case 'bedroom': return unit.bedroom ?? 0;
      case 'bathroom': return unit.bathroom ?? 0;
      case 'block': return unit.blockNumber || '-';
      case 'floor': return parsed.floor ?? '-';
      case 'stack': return parsed.stack ?? '-';
      default: return '-';
    }
  };

  const formatValue = (key: string, value: string | number): string => {
    if (['price', 'psf', 'outlay', 'installment'].includes(key)) {
      return typeof value === 'number' && value > 0 ? formatCurrency(value) : '-';
    }
    if (key === 'areaSqft') {
      return typeof value === 'number' && value > 0 ? `${formatNumber(value)} sqft` : '-';
    }
    return String(value);
  };

  // Build table: first column = label, then one column per unit (equal width, matches floor plan layout above)
  const head = [['', ...floorPlans.map(fp => {
    const soldLabel = (fp.unit.unitStatus || '').toString().toLowerCase() === 'sold' ? ' SOLD' : '';
    return `${fp.unit.projectName}\n${fp.unit.unitNumber}${soldLabel}`;
  })]];
  const body = overviewRows.map(row => {
    const values = floorPlans.map(fp => getUnitValue(fp.unit, row.key));

    // Determine best value for highlighting
    const isNumeric = ['price', 'psf', 'outlay', 'installment', 'areaSqft', 'bedroom', 'bathroom'].includes(row.key);
    const isBiggerBetter = ['areaSqft', 'bedroom', 'bathroom'].includes(row.key);
    const isFloor = row.key === 'floor';
    const numericValues = isNumeric ? (values.filter(v => typeof v === 'number' && v > 0) as number[]) : [];
    const floorValues = isFloor ? (values.filter(v => typeof v === 'number') as number[]) : [];

    let bestValue: number | null = null;
    if (isFloor && floorValues.length > 0) {
      bestValue = Math.max(...floorValues);
    } else if (isNumeric && numericValues.length > 0) {
      bestValue = isBiggerBetter ? Math.max(...numericValues) : Math.min(...numericValues);
    }

    return [row.label, ...values.map(v => {
      const display = formatValue(row.key, v);
      return display;
    })];
  });

  autoTable(doc, {
    startY: yPos,
    head,
    body,
    theme: 'grid',
    headStyles: {
      font: PDF_UNICODE_FONT,
      fillColor: CORPORATE_COLOR_RGB,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: labelColWidth, fillColor: [245, 247, 250] },
      ...Object.fromEntries(floorPlans.map((_, i) => [i + 1, { halign: 'center' as const, cellWidth: dataColWidth }])),
    },
    tableWidth: totalWidth,
    styles: { font: PDF_UNICODE_FONT, fontSize: 8, textColor: [0, 0, 0], cellPadding: 2.5 },
    margin: { left: margin, right: margin },
    didParseCell: (hookData: any) => {
      if (hookData.section === 'body' && hookData.column.index > 0) {
        const rowIndex = hookData.row.index;
        const colIndex = hookData.column.index - 1;
        const row = overviewRows[rowIndex];
        if (!row) return;

        const values = floorPlans.map(fp => getUnitValue(fp.unit, row.key));
        const value = values[colIndex];
        const isNumeric = ['price', 'psf', 'outlay', 'installment', 'areaSqft', 'bedroom', 'bathroom'].includes(row.key);
        const isBiggerBetter = ['areaSqft', 'bedroom', 'bathroom'].includes(row.key);
        const isFloor = row.key === 'floor';
        const numericValues = isNumeric ? (values.filter(v => typeof v === 'number' && v > 0) as number[]) : [];
        const floorValues = isFloor ? (values.filter(v => typeof v === 'number') as number[]) : [];

        let isBest = false;
        if (isFloor && typeof value === 'number' && floorValues.length > 0) {
          isBest = value === Math.max(...floorValues);
        } else if (isNumeric && typeof value === 'number' && value > 0 && numericValues.length > 0) {
          isBest = value === (isBiggerBetter ? Math.max(...numericValues) : Math.min(...numericValues));
        }

        if (isBest && numericValues.length > 1) {
          hookData.cell.styles.textColor = [46, 125, 50]; // green for best value
          hookData.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  // ── Footer ──
  const footerY = pageHeight - 12;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  // Huttons logo
  try {
    const logoImg = await loadImage(HuttonsWhiteLogo);
    const logoHeight = 6;
    const logoWidth = logoHeight * (logoImg.width / logoImg.height);
    doc.addImage(HuttonsWhiteLogo, 'PNG', 10, footerY + (12 - logoHeight) / 2, logoWidth, logoHeight);
  } catch {
    doc.text('Huttons', 10, footerY + 7);
  }
  const now = new Date();
  const genDate = now.toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  const genTime = now.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  doc.text(`Generated on ${genDate} ${genTime}`, pageWidth / 2, footerY + 7, { align: 'center' });
  if (userInfo) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    if (userInfo.Name) doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    if (userInfo.CEANumber) doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 9, { align: 'right' });
  }

};
